import {
  useRemoveClaimContactMutation,
  useDeleteClaimContactMutation,
  useDeleteNote,
} from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';
import WarningIcon from '@mui/icons-material/Warning';
import './delete-contact-modal.scss';
import { FC } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { NewConvergeButton } from '../../../presentation/components/new-button';
import CloseIcon from '@mui/icons-material/Close';

// Types for different deletion modes
interface ContactDeleteData {
  claimantUuid: string;
  type: string;
  claimUuid: string;
}

interface AdditionalInfoDeleteData {
  uuid: string;
}

interface NoteDeleteData {
  noteUuid: string;
}

type DeleteMode = 'contact' | 'additionalInfo' | 'note';

interface UnifiedDeleteContactModalProps {
  open: boolean;
  onClose: () => void;
  onContactDeleted?: () => void;
  onNoteSaved: () => void;
  mode: DeleteMode;
  // For contact deletion
  contactData?: ContactDeleteData;
  titleName?: string;
  // For additional info deletion
  additionalInfoData?: AdditionalInfoDeleteData;
  // For note deletion
  noteData?: NoteDeleteData;
}

export const UnifiedDeleteContactModal: FC<UnifiedDeleteContactModalProps> = ({
  open,
  onClose,
  onContactDeleted,
  onNoteSaved,
  mode,
  contactData,
  titleName,
  additionalInfoData,
  noteData,
}) => {
  const { removeClaimContact, loading: contactLoading } =
    useRemoveClaimContactMutation();
  const { deleteClaimContact, loading: additionalLoading } =
    useDeleteClaimContactMutation();
  const { deleteNote, loading: noteLoading } = useDeleteNote();
  const { notify } = useConvergeSnackBar();

  const loading =
    mode === 'contact'
      ? contactLoading
      : mode === 'note'
      ? noteLoading
      : additionalLoading;

  const handleDelete = async () => {
    try {
      if (mode === 'contact' && contactData) {
        await removeClaimContact({
          claimUuid: contactData.claimUuid,
          claimantUuid: contactData.claimantUuid,
        });

        notify(`${contactData.type} has been deleted`, {
          variant: 'success',
        });
      } else if (mode === 'additionalInfo' && additionalInfoData) {
        await deleteClaimContact(additionalInfoData.uuid);
        notify(`Additional information deleted successfully`, {
          variant: 'success',
        });
      } else if (mode === 'note' && noteData) {
        await deleteNote({ variables: { noteUuid: noteData.noteUuid } });
        notify('Note deleted successfully', { variant: 'success' });
      }

      if (onNoteSaved) {
        onNoteSaved();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting:', error);
      const errorMessage =
        mode === 'contact'
          ? `Error deleting ${contactData?.type}`
          : mode === 'note'
          ? 'Error deleting note'
          : 'Error deleting Additional information';
      notify(errorMessage, { variant: 'error' });
    }
  };

  // Determine title based on mode
  const getTitle = () => {
    if (mode === 'note') return 'Delete Note';
    if (mode === 'contact' && titleName) return titleName;
    return 'Delete Additional Contact';
  };

  // Determine message based on mode
  const getMessage = () => {
    if (mode === 'note') {
      return 'Are you sure you want to delete this note? This action cannot be undone.';
    }
    if (mode === 'contact' && contactData) {
      return `Are you sure you want to delete this ${
        contactData.type || titleName
      } from the claim? This action cannot be undone.`;
    }
    return 'Are you sure you want to delete this additional information from the claim? This action cannot be undone.';
  };

  // Determine button label based on mode
  const getButtonLabel = () => {
    if (mode === 'note') return 'DELETE NOTE';
    if (mode === 'contact' && contactData) {
      return `Delete ${contactData.type || titleName}`;
    }
    return 'DELETE CONTACT';
  };

  // Determine test ID based on mode
  const getTestId = () => {
    if (mode === 'note') return 'delete-note-btn';
    if (mode === 'contact' && contactData) {
      return `delete-${contactData.type || titleName}-btn`;
    }
    return 'delete-additional-information-btn';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={true}
      PaperProps={{
        sx: {
          width: '650px',
          height: '250px',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: 'auto',
        },
      }}
      className="delete-modal-contact"
    >
      <DialogTitle sx={{ padding: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingTop="10px"
          paddingRight="15px"
        >
          <Box className="main-title-contact-modal" sx={{ marginTop: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ marginRight: '5px' }} /> {getTitle()}
            </Box>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack>
          <Box>
            <Typography className="modal-message">{getMessage()}</Typography>
          </Box>
        </Stack>

        <Stack
          className="button-container-contact-modal"
          sx={mode === 'note' ? { marginTop: '35px !important' } : {}}
        >
          <Box className="close-btn-Container">
            <NewConvergeButton
              label="CANCEL"
              variant="outlined"
              onClick={onClose}
              data-testid="cancel-delete-contact-btn"
            />
          </Box>
          <Box>
            <NewConvergeButton
              loading={loading}
              onClick={handleDelete}
              data-testid={getTestId()}
              label={getButtonLabel()}
            />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
